/**
 * PageHeader — Cabeçalho interno de página.
 * Exibe título, subtítulo e slot de ações no topo de cada página.
 * Substitui o antigo Header global injetado via HeaderContext.
 *
 * Princípios: Single Responsibility, tokens semânticos.
 */
import React from 'react';
import { cn } from '../../lib/utils';
import { ArrowLeftIcon } from '../icons';
import { useAppContext } from '../../contexts/AppContext';

interface PageHeaderProps {
    title: React.ReactNode;
    subtitle?: string;
    action?: React.ReactNode;
    actionClassName?: string;
    className?: string;
    titleClassName?: string;
    subtitleClassName?: string;
    hideBackButton?: boolean;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    subtitle,
    action,
    actionClassName,
    className,
    titleClassName,
    subtitleClassName,
    hideBackButton = false,
}) => {
    const { goBack, canGoBack } = useAppContext();
    const showBack = canGoBack && !hideBackButton;

    return (
        <div className={cn('flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6', className)}>
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                    {showBack && (
                        <button
                            type="button"
                            onClick={goBack}
                            className="-ml-1.5 rounded-lg p-1.5 text-muted transition-colors hover:bg-surface-subtle hover:text-default"
                            title="Voltar"
                        >
                            <ArrowLeftIcon className="h-4 w-4" />
                        </button>
                    )}
                    <h1 className={cn('text-xl font-semibold tracking-tight text-default', titleClassName)}>{title}</h1>
                </div>
                {subtitle && (
                    <p className={cn('text-sm text-muted', subtitleClassName)}>
                        {subtitle}
                    </p>
                )}
            </div>

            {action && (
                <div className={cn('flex w-full min-w-0 items-center gap-2 md:w-auto md:shrink-0', actionClassName)}>
                    {action}
                </div>
            )}
        </div>
    );
};
